"use client";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { caseStudyData } from "@/data/CaseStudyData";
import { caseStudyWorkings } from "@/data/CaseStudyPageData";
import { fadeInUp } from "@/lib/animation/variant";
import { cn } from "@/lib/utils/utils";

// An asymmetric editorial grid rather than four matching tiles — the plates
// alternate wide/narrow and swap sides down the column.
const SPAN = [
  "xl:col-span-7 xl:col-start-1",
  "xl:col-span-5 xl:col-start-8",
  "xl:col-span-5 xl:col-start-1",
  "xl:col-span-7 xl:col-start-6",
];

const CaseStudyProof = () => {
  const reduceMotion = useReducedMotion();

  return (
    <section className="w-full bg-background pt-16 md:pt-24">
      <Container className="flex flex-col gap-8 px-4 lg:px-10">
        <div className="space-y-2">
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionSubHeading subtitle="Check the work — every engagement is live right now" />
          </motion.div>

          <motion.div
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionTitle title="Verify it yourself." className="max-w-4xl" />
          </motion.div>
        </div>

        <div className="mt-2 grid gap-5 md:grid-cols-2 xl:grid-cols-12 xl:gap-6">
          {caseStudyData.map((study, index) => {
            const working = caseStudyWorkings.find(
              (item) => item.slug === study.slug,
            );
            if (!working) return null;

            return (
              <motion.article
                key={study.id}
                // Plates slide in from the side they sit on and unshear as they
                // land, so they arrive differently from the straight fade-and-
                // rise every other block on the page uses.
                initial={
                  reduceMotion
                    ? { opacity: 0 }
                    : { opacity: 0, x: index % 2 === 0 ? -36 : 36, skewY: 1.4 }
                }
                whileInView={{ opacity: 1, x: 0, skewY: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.85,
                  delay: (index % 2) * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={cn(SPAN[index])}
              >
                {/* Every plate goes to our own case study, so all four detail
                    pages get an internal link from this section. */}
                <Link
                  href={`/case-study/${study.slug}`}
                  className="group relative flex h-full flex-col gap-4 overflow-hidden bg-black/[0.02] p-6 transition-colors duration-500 hover:bg-black/[0.05] md:p-8"
                >
                  {/* Client tone as the plate's top rule; lime sweeps across it
                      from the left on hover. */}
                  <span
                    aria-hidden
                    style={{ backgroundColor: study.bgColor }}
                    className="absolute inset-x-0 top-0 h-1.5"
                  />
                  <span
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-1.5 origin-left scale-x-0 bg-primary transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100"
                  />

                  {/* Ghosted index, sitting behind the type and drifting in as
                      the pointer arrives. */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -top-4 right-2 text-8xl leading-none font-black tracking-tighter text-black/[0.04] tabular-nums transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-x-3"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <p className="relative font-mono text-[10px] tracking-[0.18em] text-muted-foreground uppercase">
                    {study.tag} · {working.context}
                  </p>

                  <h3 className="relative text-3xl leading-[0.9] font-black tracking-tighter uppercase md:text-4xl">
                    {study.name ?? study.title}
                  </h3>

                  <p className="relative max-w-[46ch] text-sm leading-relaxed text-muted-foreground md:text-base">
                    {working.verify}
                  </p>

                  <div className="relative mt-auto flex items-center justify-between gap-4 border-t border-black/10 pt-4">
                    <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] lowercase">
                      <span
                        aria-hidden
                        className="size-1.5 shrink-0 rounded-full bg-primary"
                      />
                      octarnal.com/case-study/{study.slug}
                    </span>

                    {/* The arrow leaves to the top-right and a second one takes
                        its place from the bottom-left. */}
                    <span className="relative grid size-9 shrink-0 place-items-center overflow-hidden border border-black/15 transition-colors duration-500 group-hover:border-primary">
                      <ArrowUpRight className="size-4 [grid-area:1/1] transition-transform duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-6 group-hover:translate-x-6" />
                      <ArrowUpRight className="size-4 translate-x-[-1.5rem] translate-y-6 [grid-area:1/1] text-primary transition-transform duration-400 delay-75 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-0 group-hover:translate-y-0" />
                    </span>
                  </div>
                </Link>
              </motion.article>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default CaseStudyProof;
